import { InstagramIntegration } from 'api/clients/ExternalIntegrationsClient'
import Styles from './InstagramPreview.module.scss'
import { BookmarkIcon, CommentIcon, LikeIcon, ShareIcon } from './icons'
import Silhouette from './silhouette.jpg'
import React, { ReactNode, useRef, useState } from 'react'
import { CarouselDots } from './CarouselDots'
import { MoreButton } from '../components/MoreButton/MoreButton'
import { useTruncateText } from '../hooks/useTruncateText'
import { Carousel } from 'components/Carousel/Carousel'
import { ConditionalWrapper } from 'common/ConditionalWrapper/ConditionalWrapper'
import { getIntegrationDetails } from '../getIntegrationDetails'
import { PollingPreview } from '../components/PollingPreview/PollingPreview'
import { ChannelType } from 'components/CampaignCreationModal/Types'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { Skeleton } from 'antd'
import cx from 'classnames'
import { highlightContent } from '../highlightContent'
import { ConnectButton } from 'components/ArticlePostPreview/ConnectButton/ConnectButton'
import { MediaObject } from 'types'
import { MediaRenderer } from '../components/MediaRenderer/MediaRenderer'
import { useFlipperFlag } from 'hooks/useFlipperFlag'
import { Edit1 } from 'blaze-ui/icons'
import { Button } from 'blaze-ui/Button'
import * as CSS from 'csstype'
import { Image03 } from 'blaze-ui/icons/24'
import { calculateAspectRatio } from './utils'
import { EditCaptionOverlay } from '../components/EditCaptionOverlay/EditCaptionOverlay'
import { ScheduledPostShow } from 'api/clients/ScheduledPostsClient'

interface InstagramPreviewProps {
  integration?: InstagramIntegration
  content: string
  mediaObjects?: MediaObject[]
  loading?: boolean
  emptyStateContent?: React.ReactNode
  pollContentStream?: boolean
  hideConnectButton?: boolean
  onEdit?: () => void
  containerClassName?: string
  customPreviewContent?: ReactNode
  maxWidth?: number
  showVideoControls?: boolean
  isActive?: boolean
  isDesignBlank?: boolean
  isInsertingMedia?: boolean
  isTransitioning?: boolean
  isGenerating?: boolean
  showHoverActionOverlay?: boolean
  almanacFileKey?: string
  onClose?: () => void
  scheduledPost?: ScheduledPostShow
  alwaysTruncateCaption?: boolean
  captionElement?: React.ReactNode
  defaultName?: string
}

const DefaultEmptyStateContent = () => (
  <div className={Styles.imageContainer}>
    <div className={Styles.imagePlaceholder}>
      <div className={Styles.imagePlaceholderIcon}>
        <Image03 size={42} />
      </div>
    </div>
  </div>
)

export const InstagramPreview = ({
  mediaObjects,
  content,
  integration,
  loading,
  emptyStateContent = <DefaultEmptyStateContent />,
  pollContentStream = true,
  hideConnectButton,
  onEdit,
  containerClassName,
  customPreviewContent,
  showVideoControls,
  maxWidth = 470,
  isActive = true,
  isDesignBlank = false,
  isInsertingMedia = false,
  isTransitioning = false,
  isGenerating = false,
  showHoverActionOverlay = false,
  almanacFileKey,
  onClose,
  scheduledPost,
  alwaysTruncateCaption = false,
  captionElement = null,
  defaultName,
}: InstagramPreviewProps) => {
  const postRef = useRef<HTMLDivElement>(null)
  const { name, picture } = getIntegrationDetails(integration, defaultName)
  const [imageIndex, setImageIndex] = useState(0)
  const [imageFailed, setImageFailed] = useState(false)
  const { isMobile } = useResponsiveDesign()
  const [imagesLoading, setImagesLoading] = useState(false)
  const isPostPreviewV2 = useFlipperFlag('post_preview_v2')
  const { isTruncated, setIsTruncated, shouldShowMoreButton, textRef } = useTruncateText({
    content,
    truncatedByDefault:
      alwaysTruncateCaption || ((!isMobile || isPostPreviewV2) && (!!mediaObjects?.length || !!customPreviewContent)),
  })

  const images = mediaObjects?.map((mediaObject) => mediaObject.thumbnail_url || mediaObject.source_url)

  const highlightClassNames = {
    mentionClassName: Styles.highlight,
    hashtagClassName: Styles.highlight,
  }

  const style: CSS.Properties = {
    ['--max-width' as any]: `${maxWidth}px`,
  }

  return (
    <div className={cx(Styles.post, containerClassName)} ref={postRef} data-testid="ig-preview" style={style}>
      <header className={Styles.header}>
        <div className={Styles.user}>
          {imageFailed ? (
            <img className={Styles.avatar} src={Silhouette} alt="Avatar" />
          ) : (
            <img className={Styles.avatar} src={picture} alt="Avatar" onError={() => setImageFailed(true)} />
          )}

          <div className={Styles.info}>
            <div className={Styles.username}>{name}</div>
          </div>

          {onEdit && (
            <div className={Styles.editButton}>
              <Button variant="tertiary" onPress={onEdit} frontIcon={Edit1}>
                Edit
              </Button>
            </div>
          )}
          {!hideConnectButton && isMobile && <ConnectButton />}
        </div>
      </header>
      {isDesignBlank ? (
        <>{loading ? <DefaultEmptyStateContent /> : emptyStateContent}</>
      ) : (
        customPreviewContent || (
          <>
            {images && images.length > 0 ? (
              <ConditionalWrapper
                condition={images.length > 1}
                wrapper={(children) => (
                  <Carousel
                    fullWidth
                    withControls={false}
                    onSlideScrolled={(e) => setImageIndex(e.selectedScrollSnap())}
                    options={{ startIndex: imageIndex }}
                  >
                    {children}
                  </Carousel>
                )}
              >
                {mediaObjects?.filter(Boolean).map((mediaObject, index) => {
                  const imgSrc = mediaObject.thumbnail_url || mediaObject.source_url || ''

                  return (
                    <div key={`igImage-${index}-${imgSrc}`} className={Styles.imagePreviewContainer}>
                      {captionElement}
                      <MediaRenderer
                        mediaObject={mediaObject}
                        alt=""
                        showHoverActionOverlay={showHoverActionOverlay}
                        onClose={onClose}
                        almanacFileKey={almanacFileKey}
                        isGenerating={isGenerating}
                        showVideoControls={showVideoControls}
                        isActive={isActive}
                        isPressable={!showHoverActionOverlay}
                        style={{
                          aspectRatio: calculateAspectRatio(mediaObject),
                          width: '100%',
                          background: 'var(--dark-100)',
                        }}
                      />
                    </div>
                  )
                })}
              </ConditionalWrapper>
            ) : (
              <>
                {(imagesLoading && !images?.length) || isInsertingMedia ? (
                  <Skeleton className={Styles.imageContainer} active paragraph={{ rows: 1 }} title={false} />
                ) : (
                  <>{loading ? <DefaultEmptyStateContent /> : emptyStateContent}</>
                )}
              </>
            )}
          </>
        )
      )}
      <ConditionalWrapper
        condition={showHoverActionOverlay && almanacFileKey}
        wrapper={(children) => {
          if (!scheduledPost || !almanacFileKey) return children

          return (
            <EditCaptionOverlay
              scheduledPost={scheduledPost}
              almanacFileKey={almanacFileKey}
              borderRadius={0}
              position="top-right"
              onClose={onClose}
            >
              {children}
            </EditCaptionOverlay>
          )
        }}
      >
        <div className={Styles.postContent}>
          {!isPostPreviewV2 && (
            <div className={Styles.actions}>
              <div className={Styles.actionsLeft}>
                <LikeIcon />
                <CommentIcon />
                <ShareIcon />
              </div>
              {images && images.length > 1 && !customPreviewContent && (
                <CarouselDots
                  count={images.length}
                  activeIndex={imageIndex}
                  setActiveIndex={(index: number) => setImageIndex(index)}
                />
              )}

              <div className={Styles.actionsRight}>
                <BookmarkIcon />
              </div>
            </div>
          )}
          {!isPostPreviewV2 && !isMobile && <div className={Styles.likes}>50,024</div>}
          <div className={Styles.caption}>
            {loading && pollContentStream && !customPreviewContent ? (
              <>
                <span className={cx(Styles.username, { [Styles.loading]: loading })}>{name}</span>
                <PollingPreview
                  setImagesLoading={setImagesLoading}
                  imagesLoading={imagesLoading}
                  channel={ChannelType.InstagramPosts}
                />
              </>
            ) : (
              <>
                {isMobile ? (
                  <>
                    <span
                      ref={textRef as React.RefObject<HTMLSpanElement>}
                      key={`caption-${content}`}
                      className={cx({ [Styles.truncated]: isTruncated })}
                      dangerouslySetInnerHTML={{
                        __html: highlightContent(`<strong>${name}</strong> ${content}`, highlightClassNames),
                      }}
                    />
                    {shouldShowMoreButton && !alwaysTruncateCaption && (
                      <MoreButton isTruncated={isTruncated} setIsTruncated={setIsTruncated} />
                    )}
                  </>
                ) : (
                  <>
                    <span className={Styles.username}>{name}</span>{' '}
                    <span
                      ref={textRef as React.RefObject<HTMLSpanElement>}
                      key={`caption-${content}`}
                      className={cx({ [Styles.truncated]: isTruncated })}
                      dangerouslySetInnerHTML={{
                        __html: highlightContent(content, highlightClassNames),
                      }}
                    />
                    {shouldShowMoreButton && !alwaysTruncateCaption && (
                      <MoreButton isTruncated={isTruncated} setIsTruncated={setIsTruncated} />
                    )}
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </ConditionalWrapper>
    </div>
  )
}
